/** Locale of the whole interface: every date, number and label shown to people is Spanish. */
export const LOCALE = 'es'

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
const DASH = '—'

function parseIso(text: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(text)
  if (!m) return null
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return DASH
  const d = parseIso(iso)
  if (!d) return iso
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return DASH
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const hh = String(d.getUTCHours()).padStart(2, '0')
  const mm = String(d.getUTCMinutes()).padStart(2, '0')
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}, ${hh}:${mm} UTC`
}

/** The banner shown wherever a condition (band, score) is displayed. */
export function stalenessMessage(inspectedOn: string | null | undefined): string {
  if (!inspectedOn) return 'Fecha de inspección desconocida — validar antes de considerarla vigente'
  return `Datos del ${formatDate(inspectedOn)} — validar antes de considerarlos vigentes`
}

export function daysBetween(fromIso: string, toIso: string): number {
  const a = parseIso(fromIso)
  const b = parseIso(toIso)
  if (!a || !b) return Number.NaN
  return Math.round((b.getTime() - a.getTime()) / 86_400_000)
}

export function formatNumber(value: string | number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || value === '') return DASH
  const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'))
  return Number.isFinite(n) ? n.toFixed(digits) : DASH
}

export function formatPercent(value: string | number | null | undefined, digits = 1): string {
  const text = formatNumber(value, digits)
  return text === DASH ? DASH : `${text} %`
}

export function formatInteger(value: number | null | undefined): string {
  if (value === null || value === undefined) return DASH
  return new Intl.NumberFormat(LOCALE).format(value)
}

export function formatLitres(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return DASH
  const n = Number(value)
  return Number.isFinite(n) ? `${Number(n.toFixed(2))} L` : DASH
}

/** Data-quality issue kinds (the API also sends an English label; the interface shows these). */
export const ISSUE_KIND_LABELS: Record<string, string> = {
  bad_file: 'Archivo ilegible o rechazado',
  duplicate: 'Registro de origen duplicado',
  name_conflict: 'Conflicto de nombre',
  code_conflict: 'Conflicto de código',
  placeholder_code: 'Código provisional o faltante',
  out_of_domain: 'Valor fuera del conjunto definido',
  all_zero_row: 'Fila en cero (sin evaluar)',
  zero_placeholder: 'Cero en un criterio cuyo valor normal no es cero',
  unmapped_token: 'Texto libre sin equivalencia',
  value_mismatch: 'El valor digitado difiere del valor de la plantilla',
  total_mismatch: 'El total de la hoja difiere de la tabla o del total recalculado',
  date_mismatch: 'Fecha faltante, sospechosa o distinta entre fuentes',
  narrative_mismatch: 'La narrativa del informe difiere de la tabla',
  unmatched_name: 'Fila de origen sin equipo correspondiente',
  load_anomaly: 'Lectura de carga por revisar',
}

/** Display labels for the codes the API sends (statuses, severities, sources, roles, import counters...). */
const CODE_LABELS: Record<string, string> = {
  ...ISSUE_KIND_LABELS,
  // statuses
  open: 'Abierto', acknowledged: 'Reconocido', resolved: 'Resuelto', dismissed: 'Descartado',
  assigned: 'Asignado', in_progress: 'En curso', done: 'Finalizado', verified: 'Verificado', cancelled: 'Cancelado',
  draft: 'Borrador', submitted: 'Enviado', validated: 'Validado', committed: 'Confirmado', uploaded: 'Cargado', failed: 'Fallido',
  published: 'Publicado', retired: 'Retirado', recommended: 'Recomendado',
  // severities, priorities and levels
  info: 'Información', warning: 'Advertencia', error: 'Error', critical: 'Crítica', medium: 'Media', low: 'Baja', high: 'Alta', urgent: 'Urgente',
  normal: 'Normal', not_applicable: 'No aplica', not_measured: 'Sin medir', missing: 'Faltante',
  // bands
  stable: 'Estable', preventive: 'Preventivo', not_assessed: 'Sin evaluar',
  // operating state and next due
  running: 'En marcha', off: 'Apagado', unknown: 'Desconocido', ok: 'Al día', due_soon: 'Próximo a vencer', overdue: 'Vencido',
  // sources and dates
  manual: 'Manual', import: 'Importación', consolidated_report: 'Informe consolidado', equipment_sheet: 'Ficha del equipo',
  auto_suggested: 'Sugerido automáticamente', upload: 'Carga de archivos', server_path: 'Ruta del servidor',
  date_unknown: 'Fecha desconocida', date_suspect: 'Fecha sospechosa',
  // lubrication
  mineral: 'Mineral', synthetic: 'Sintético', change: 'Cambio de aceite', level_check: 'Revisión de nivel', top_up: 'Reposición de aceite',
  // roles
  admin: 'Administrador', engineer: 'Ingeniero', inspector: 'Inspector', viewer: 'Visualizador',
  // alert kinds
  critical_entry: 'Entrada en estado crítico', route_critical_at_risk: 'Equipo de ruta crítica en riesgo', score_worsened: 'Puntaje empeorado',
  inspection_overdue: 'Inspección vencida', data_stale: 'Datos desactualizados', work_order_overdue: 'Orden de trabajo vencida',
  critical_finding_unassigned: 'Hallazgo crítico sin asignar', lubrication_due: 'Lubricación por vencer', remeasure_pending: 'Nueva medición pendiente',
  data_quality_open: 'Calidad de datos abierta',
  // import summary and commit counters
  files_total: 'Archivos en total', files_rejected: 'Archivos rechazados', files_skipped_duplicates: 'Archivos duplicados omitidos',
  workbooks: 'Libros de cálculo', equipment_new: 'Equipos nuevos', equipment_generated_codes: 'Equipos con código generado',
  route_critical: 'Equipos de ruta crítica', sheet_inspections: 'Inspecciones de fichas', sheet_dates_unknown: 'Fichas con fecha desconocida',
  sheet_dates_suspect: 'Fichas con fecha sospechosa', consolidated_rows: 'Filas consolidadas', consolidated_critical: 'Críticos consolidados',
  consolidated_critical_reported: 'Críticos reportados en el consolidado', consolidated_not_assessed: 'Sin evaluar en el consolidado',
  consolidated_all_zero: 'Filas en cero en el consolidado', consolidated_out_of_domain_cells: 'Celdas fuera de dominio en el consolidado',
  loads_rows: 'Filas de cargas', loads_off: 'Cargas apagadas', loads_div0_cells: 'Celdas de cargas con división por cero',
  loads_not_measured: 'Cargas sin medir', loads_needs_remeasure: 'Cargas que requieren nueva medición', loads_measured: 'Cargas medidas',
  inspections_new: 'Inspecciones nuevas', inspections_existing: 'Inspecciones existentes', measurements_new: 'Mediciones nuevas',
  measurements_existing: 'Mediciones existentes', issues_total: 'Problemas en total',
  equipment_created: 'Equipos creados', equipment_existing: 'Equipos existentes', inspections_created: 'Inspecciones creadas',
  measurements_created: 'Mediciones creadas', issues_created: 'Problemas creados', issues_existing: 'Problemas existentes',
  route_critical_marked: 'Equipos de ruta crítica marcados', units_committed: 'Unidades confirmadas', units_failed: 'Unidades fallidas',
}

/** Criterion levels (the answer an inspector gives to each criterion). */
const LEVEL_LABELS: Record<string, string> = {
  normal: 'Normal', medium: 'Medio', critical: 'Crítico', not_applicable: 'No aplica', not_measured: 'Sin medir', missing: 'Faltante',
}

export function levelLabel(level: string | null | undefined): string {
  if (!level) return DASH
  return LEVEL_LABELS[level] ?? humanize(level)
}

/** Label of a data-quality issue kind: the Spanish one when known, else what the API sent. */
export function kindLabel(kind: string, fallback?: string): string {
  return ISSUE_KIND_LABELS[kind] ?? fallback ?? humanize(kind)
}

export function humanize(code: string | null | undefined): string {
  if (!code) return DASH
  const known = CODE_LABELS[code]
  if (known) return known
  const text = code.replace(/_/g, ' ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export function dateRangeLabel(from?: string, to?: string): string {
  if (from && to) return `${formatDate(from)} – ${formatDate(to)}`
  if (to) return `Hasta ${formatDate(to)}`
  if (from) return `Desde ${formatDate(from)}`
  return 'Última inspección de cada equipo'
}

export function todayIso(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}
